import { Card, Confirmation_, Input_, PageFull, PageSidebar, PageTitle, s3 } from 'monica-alexandria';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Toolbar_ } from '../../partials/navigation/Toolbar_';
import { Sass_Brand_Slider } from './components/Sass_Brand_Slider';
import { Sass_Offer_Card } from './components/Sass_Offer_Card';
import { list_sass_board_offers, list_sass_business_options_by_id, list_sass_categories, list_sass_timeline_by_id, match_prospect_user_by_username, update_sass_board_status_by_id, update_sass_comment_by_id } from '../../../services/api/Offers';
import { useHrAccess } from '../../../services/authentication/HrAccess';
import { get_route_language } from '../../../helpers/routeLanguage';
import {
    offer_client_name_from_offer,
    offer_current_status,
    offer_emails_from_offer,
    offer_numbers_from_offer,
    offer_reference_from_offer,
    selected_types_from_offer
} from '../../../helpers/offerObject';
import { object_matches_search } from '../../../helpers/searchValues';
import {
    read_sass_paid_card_requirements,
    read_sass_paid_requirements_message
} from './utils/sass_paid_requirements';
import { read_sass_offer_registration_id } from './utils/sass_offer_identity';
import { read_sass_workflow_status } from './utils/sass_workflow_status';
import {
    read_sass_offer_with_meeting_date,
    read_sass_selected_offer_after_refresh
} from './utils/sass_board_updates';
import { useTimelinePages } from '../../../utils/timeline/use_timeline_pages';
import {
    read_current_sass_assignee,
    sass_assignee_matches_user,
    read_sass_drag_allowed,
    read_sass_team_member_options,
    sass_move_requires_confirmation
} from './utils/sass_assignment_permissions';

const match_sass_account_username = async(username = '') => {
    const response = await match_prospect_user_by_username(username);

    return response?.data?.data || {};
};

export default function SASS() {
    const { t, i18n } = useTranslation();
    const user = useSelector((state) => state.user.user || {});
    const { members } = useHrAccess();
    const [sidebar, setSidebar] = useState(false);
    const [offers, setOffers] = useState([]);
    const [nifflerCategoryMap, setNifflerCategoryMap] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedSassOffer, setSelectedSassOffer] = useState(null);
    const timelinePage = useTimelinePages({
        identity: offer_identity_value(selectedSassOffer), refreshKey: selectedSassOffer, t,
        readPage: cursor => list_sass_timeline_by_id(offer_identity_value(selectedSassOffer), cursor).then(response => response?.data?.data)
    });
    const { events: timelineEvents, hasMore: timelineHasMore, loading: timelineLoading,
        loadingMore: timelineLoadingMore, error: timelineError, warning: timelineWarning,
        publication: timelinePublication, load_more: load_more_sass_timeline, retry: retry_sass_timeline } = timelinePage;
    const [dragOfferId, setDragOfferId] = useState('');
    const [pendingSassReassignment, setPendingSassReassignment] = useState(null);
    const [sassSearchInput, setSassSearchInput] = useState('');
    const [selectedSassSellerKeys, setSelectedSassSellerKeys] = useState([]);
    const dragOfferIdRef = useRef('');
    const dragHandleOfferIdRef = useRef('');
    const dragClickLockRef = useRef(false);
    const routeLanguage = get_route_language(i18n.language);
    const boardOffers = offers.filter((offerItem) => offer_is_sass(offerItem));
    const sellerOptions = sass_board_seller_options(boardOffers);
    const teamMemberOptions = read_sass_team_member_options(members);
    const currentSassAssignee = read_current_sass_assignee(user, teamMemberOptions);
    const currentUserCanClaim = teamMemberOptions.some((memberItem) => sass_assignee_matches_user(memberItem, user));
    const currentUserCanManageAssignments = read_sass_assignment_admin(user);
    const visibleOffers = filtered_sass_board_offers(boardOffers, {
        searchValue: sassSearchInput,
        selectedSellerKeys: selectedSassSellerKeys
    });
    const offersByColumn = offers_by_column(visibleOffers);

    useEffect(() => {
        const get_offers = async() => {
            setIsLoading(true);
            setErrorMessage('');

            try {
                const response = await list_sass_board_offers();
                setOffers(response?.data?.data || []);
            } catch (error) {
                setErrorMessage(t('Could not load offers.'));
            } finally {
                setIsLoading(false);
            }
        };

        get_offers();
    }, [t]);

    useEffect(() => {
        const get_categories = async() => {
            try {
                const response = await list_sass_categories();
                setNifflerCategoryMap(read_sass_categories_from_response(response));
            } catch (error) {
                setNifflerCategoryMap({});
            }
        };

        get_categories();
    }, []);

    const toggle_sass_seller_filter = (sellerKey = '') => {
        setSelectedSassSellerKeys((previousValues) => {
            const alreadySelected = previousValues.includes(sellerKey);

            if (alreadySelected) {
                return previousValues.filter((valueItem) => valueItem !== sellerKey);
            }

            return [...previousValues, sellerKey];
        });
    };

    const refresh_offer_in_board = (nextOffer = {}) => {
        const offerIdentity = offer_identity_value(nextOffer);

        if (!offerIdentity.length) {
            return;
        }

        if (nextOffer.deleted === true) {
            setOffers((previousOffers) => {
                return previousOffers.filter((offerItem) => offer_identity_value(offerItem) !== offerIdentity);
            });

            if (selectedSassOffer && offer_identity_value(selectedSassOffer) === offerIdentity) {
                setSelectedSassOffer(null);
            }

            return;
        }

        setOffers((previousOffers) => {
            return previousOffers.map((offerItem) => {
                return offer_identity_value(offerItem) === offerIdentity ? nextOffer : offerItem;
            });
        });

        setSelectedSassOffer((currentSelectedOffer) => {
            return read_sass_selected_offer_after_refresh(currentSelectedOffer, nextOffer);
        });
    };

    const submit_sass_comment = async(offerItem = {}, commentValue = '') => {
        const targetOfferId = offer_identity_value(offerItem);
        const commentAuthor = current_comment_user_label(user);
        const commentCreatedAt = new Date().toISOString();
        const commentId = `comment_${Date.now()}`;

        if (!targetOfferId.length || !commentValue.length) {
            return false;
        }

        const nextComment = {
            id: commentId,
            sourceKey: `godfather:sass-comment:${targetOfferId}:${commentId}`,
            sourceApp: 'godfather',
            sourceEntity: 'sass',
            body: commentValue,
            html: commentValue,
            createdAt: commentCreatedAt,
            createdBy: commentAuthor,
            createdByUserId: `${user?.subId || user?.sub || user?.userId || user?.id || user?._id || ''}`,
            createdByEmail: `${user?.email || user?.preferred_username || ''}`,
            createdByUsername: `${user?.username || user?.preferred_username || ''}`
        };
        const nextComments = [
            ...read_array_values(offerItem?.comments),
            nextComment
        ];

        try {
            const updateResponse = await update_sass_comment_by_id(targetOfferId, {
                comments: nextComments,
                currentUserId: current_user_id(user),
                updatedByUserId: current_user_id(user),
                updatedByEmail: `${user?.email || ''}`,
                updatedByUsername: `${user?.username || user?.preferred_username || ''}`,
                updatedBy: commentAuthor
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (updatedOffer?._id) {
                refresh_offer_in_board(updatedOffer);
            }

            return updatedOffer;
        } catch (error) {
            return false;
        }
    };

    const save_sass_account_data = async(offerItem = {}, accountPayload = {}) => {
        const targetOfferId = offer_identity_value(offerItem);
        const nextStatus = sass_column_key(offerItem);
        const currentUserId = current_user_id(user);
        const lockUserId = sass_lock_user_id(offerItem, user);

        if (!targetOfferId.length) {
            return false;
        }

        try {
            const updateResponse = await update_sass_board_status_by_id(targetOfferId, {
                sassBoardStatus: nextStatus,
                accountData: accountPayload,
                lockOwnerUserId: lockUserId,
                userId: lockUserId,
                currentUserId,
                updatedByUserId: currentUserId,
                updatedBy: current_comment_user_label(user)
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (offer_identity_value(updatedOffer).length) {
                refresh_offer_in_board(updatedOffer);
                setSelectedSassOffer(updatedOffer);
                return updatedOffer;
            }

            refresh_offer_in_board(offerItem);
            setSelectedSassOffer(offerItem);
            return {
                saveError: 'Board save failed.'
            };
        } catch (error) {
            refresh_offer_in_board(offerItem);
            setSelectedSassOffer(offerItem);
            return {
                saveError: board_save_error_message(error)
            };
        }
    };

    const load_sass_business_options = async(offerItem = {}, accountPayload = {}) => {
        const targetOfferId = offer_identity_value(offerItem);
        const currentAccountData = sass_account_data(offerItem);

        if (!targetOfferId.length) {
            return {};
        }

        const response = await list_sass_business_options_by_id(targetOfferId, {
            username: accountPayload.accountUsername || currentAccountData.accountUsername || currentAccountData.username,
            accountUserId: accountPayload.accountUserId || currentAccountData.accountUserId,
            brandName: accountPayload.brandName || currentAccountData.brandName,
            facebookPageName: currentAccountData.facebookPageName
        });

        return response?.data?.data || {};
    };

    const save_sass_assigned_caller = async(offerItem = {}, assignedCaller = {}) => {
        const targetOfferId = offer_identity_value(offerItem);
        const nextStatus = sass_column_key(offerItem);
        const nextAssignedCaller = read_object_value(assignedCaller);
        const currentUserId = current_user_id(user);
        const lockUserId = sass_lock_user_id(offerItem, user);

        if (!targetOfferId.length || !Object.keys(nextAssignedCaller).length) {
            return false;
        }

        const nextOffer = sass_offer_with_assigned_caller(offerItem, nextStatus, nextAssignedCaller);

        try {
            const updateResponse = await update_sass_board_status_by_id(targetOfferId, {
                sassBoardStatus: nextStatus,
                assignedCaller: nextAssignedCaller,
                lockOwnerUserId: lockUserId,
                userId: lockUserId,
                currentUserId,
                updatedByUserId: currentUserId,
                updatedBy: current_comment_user_label(user)
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (offer_identity_value(updatedOffer).length) {
                refresh_offer_in_board(updatedOffer);
                setSelectedSassOffer(updatedOffer);
                return updatedOffer;
            }

            refresh_offer_in_board(offerItem);
            setSelectedSassOffer(offerItem);
            return {
                saveError: 'Board save failed.'
            };
        } catch (error) {
            if (error.cancelled) return false;
            refresh_offer_in_board(offerItem);
            setSelectedSassOffer(offerItem);
            return {
                saveError: board_save_error_message(error)
            };
        }
    };

    const mark_drag_handle_down = (event, offerId = '') => {
        dragHandleOfferIdRef.current = `${offerId || ''}`.trim();
        event.stopPropagation();
    };

    const read_offer_drag_allowed = (offerItem = {}) => {
        return read_sass_drag_allowed(
            offer_identity_value(offerItem),
            sass_column_key(offerItem),
            sass_caller(offerItem),
            user
        );
    };

    const start_drag_offer = (event, offerId = '') => {
        const targetOfferId = `${offerId || ''}`.trim();
        const targetOffer = offers.find((offerItem) => offer_identity_value(offerItem) === targetOfferId);
        const dragStartedFromHandle = dragHandleOfferIdRef.current === targetOfferId;

        if (!dragStartedFromHandle || !targetOfferId.length || !read_offer_drag_allowed(targetOffer || {})) {
            event.preventDefault();
            return;
        }

        dragOfferIdRef.current = targetOfferId;
        dragClickLockRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', targetOfferId);
        setDragOfferId(targetOfferId);
    };

    const release_drag_offer = (lockDelay = 150) => {
        setDragOfferId('');
        dragOfferIdRef.current = '';
        dragHandleOfferIdRef.current = '';

        window.setTimeout(() => {
            dragClickLockRef.current = false;
        }, lockDelay);
    };

    const finish_drag_offer = () => {
        release_drag_offer();
    };

    const allow_offer_drop = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const read_drag_offer_id = (event) => {
        const transferredOfferId = `${event?.dataTransfer?.getData('text/plain') || ''}`.trim();
        return transferredOfferId || dragOfferIdRef.current || dragOfferId;
    };

    const move_offer_to_column = async(droppedOffer = {}, nextStatus = '', assignedCaller = null) => {
        const nextTimeline = timeline_for_drag_status(droppedOffer, nextStatus);
        const offerIdentity = offer_identity_value(droppedOffer);
        const fromColumnKey = sass_column_key(droppedOffer);
        const currentUserId = current_user_id(user);
        const nextAssignedCaller = read_object_value(assignedCaller);
        const hasAssignedCaller = Object.keys(nextAssignedCaller).length > 0;
        const lockUserId = hasAssignedCaller
            ? `${nextAssignedCaller?.userId || currentUserId || ''}`.trim()
            : sass_lock_user_id(droppedOffer, user);

        if (!nextStatus.length || nextStatus === fromColumnKey) {
            return false;
        }

        if (nextStatus === 'in_progress') {
            const requiredFieldsError = sass_required_fields_error(droppedOffer, t, nextAssignedCaller);

            if (requiredFieldsError.length) {
                window.alert(requiredFieldsError);
                setSelectedSassOffer(droppedOffer);
                return false;
            }
        }

        if (nextStatus === 'paid') {
            const paidRequirements = read_sass_paid_card_requirements(droppedOffer);

            if (!paidRequirements.valid) {
                window.alert(t(read_sass_paid_requirements_message(paidRequirements)));
                setSelectedSassOffer(droppedOffer);
                return false;
            }
        }

        const nextOfferWithStatus = {
            ...droppedOffer,
            timeline: nextTimeline,
            setupData: {
                ...(droppedOffer?.setupData || {}),
                sassBoardStatus: nextStatus
            },
            typesData: {
                ...(droppedOffer?.typesData || {}),
                sassBoardStatus: nextStatus
            }
        };
        const nextOffer = hasAssignedCaller
            ? sass_offer_with_assigned_caller(nextOfferWithStatus, nextStatus, nextAssignedCaller)
            : nextOfferWithStatus;

        if (nextStatus !== 'in_progress') {
            setOffers(previousOffers => previousOffers.map(offerItem =>
                offer_identity_value(offerItem) === offerIdentity ? nextOffer : offerItem));
        }

        try {
            const updateResponse = await update_sass_board_status_by_id(offerIdentity, {
                sassBoardStatus: nextStatus,
                timeline: nextTimeline,
                ...(hasAssignedCaller ? { assignedCaller: nextAssignedCaller } : {}),
                ...(nextStatus === 'in_progress' && hasAssignedCaller ? { claimForCurrentUser: true } : {}),
                lockOwnerUserId: lockUserId,
                userId: lockUserId,
                currentUserId,
                updatedByUserId: currentUserId,
                updatedBy: current_comment_user_label(user)
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (updatedOffer?._id) {
                refresh_offer_in_board(updatedOffer);

                if (sass_column_key(updatedOffer) === 'in_progress') {
                    setSelectedSassOffer(updatedOffer);
                }

                return updatedOffer;
            }

            window.alert(t('Board save failed.'));
            setOffers((previousOffers) => {
                return previousOffers.map((offerItem) => {
                    return offer_identity_value(offerItem) === offerIdentity ? droppedOffer : offerItem;
                });
            });
            return false;
        } catch (error) {
            if (!error.cancelled) window.alert(t(board_save_error_message(error)));
            setOffers((previousOffers) => {
                return previousOffers.map((offerItem) => {
                    return offer_identity_value(offerItem) === offerIdentity ? droppedOffer : offerItem;
                });
            });
            return false;
        }
    };

    const drop_offer_on_column = async(event, columnKey = '') => {
        event.preventDefault();
        event.stopPropagation();

        const droppedOfferId = read_drag_offer_id(event);
        const targetColumn = `${columnKey || ''}`.trim();
        const droppedOffer = offers.find((offerItem) => offer_identity_value(offerItem) === droppedOfferId);
        const nextStatus = read_sass_board_status_key(targetColumn);

        if (!droppedOfferId.length || !targetColumn.length || !droppedOffer || !read_offer_drag_allowed(droppedOffer)) {
            release_drag_offer();
            return;
        }

        if (!nextStatus.length || nextStatus === sass_column_key(droppedOffer)) {
            release_drag_offer();
            return;
        }

        if (sass_move_requires_confirmation(
            sass_column_key(droppedOffer),
            nextStatus,
            sass_caller(droppedOffer),
            user
        )) {
            const requiredFieldsError = sass_required_fields_error(droppedOffer, t, currentSassAssignee);

            if (requiredFieldsError.length) {
                release_drag_offer();
                window.alert(requiredFieldsError);
                setSelectedSassOffer(droppedOffer);
                return;
            }

            setPendingSassReassignment({
                offerId: droppedOfferId,
                nextStatus
            });
            release_drag_offer();
            return;
        }

        release_drag_offer();
        await move_offer_to_column(droppedOffer, nextStatus);
    };

    const confirm_sass_reassignment = async() => {
        const pendingReassignment = pendingSassReassignment;
        const droppedOffer = offers.find((offerItem) => {
            return offer_identity_value(offerItem) === pendingReassignment?.offerId;
        });
        const nextAssignedCaller = read_current_sass_assignee(user, teamMemberOptions);

        setPendingSassReassignment(null);

        if (!droppedOffer || !Object.keys(nextAssignedCaller).length) {
            window.alert(t('Could not identify the current user.'));
            return false;
        }

        return move_offer_to_column(droppedOffer, pendingReassignment.nextStatus, nextAssignedCaller);
    };

    const claim_unassigned_sass = async(offerItem = {}) => {
        if (!currentUserCanClaim || sass_caller(offerItem) || sass_column_key(offerItem) !== 'set') {
            return false;
        }

        setPendingSassReassignment({
            offerId: offer_identity_value(offerItem),
            nextStatus: 'in_progress'
        });
    };

    const submit_sass_outcome = async(offerItem = {}, outcomePayload = {}) => {
        const targetOfferId = offer_identity_value(offerItem);
        const nextStatus = read_sass_outcome_board_status(outcomePayload.outcome);
        const accountPayload = read_object_value(outcomePayload.accountData);
        const offerWithAccountData = Object.keys(accountPayload).length
            ? sass_offer_with_account_data(offerItem, sass_column_key(offerItem), accountPayload)
            : offerItem;
        const nextTimeline = timeline_for_drag_status(
            offerWithAccountData,
            nextStatus,
            read_sass_outcome_note(outcomePayload),
            sass_seller(offerWithAccountData)
        );
        const nextComments = sass_comments_for_outcome(offerWithAccountData, outcomePayload, user);
        const nextOffer = sass_offer_with_outcome(offerWithAccountData, nextStatus, outcomePayload, nextTimeline, nextComments);
        const currentUserId = current_user_id(user);
        const lockUserId = sass_lock_user_id(offerItem, user);

        if (!targetOfferId.length || !nextStatus.length) {
            return false;
        }

        setOffers((previousOffers) => {
            return previousOffers.map((currentOffer) => {
                return offer_identity_value(currentOffer) === targetOfferId ? nextOffer : currentOffer;
            });
        });

        try {
            const updateResponse = await update_sass_board_status_by_id(targetOfferId, {
                sassBoardStatus: nextStatus,
                outcome: outcomePayload.outcome,
                rescheduledAt: outcomePayload.rescheduledAt,
                sassMeetingDate: outcomePayload.rescheduledAt,
                accountUsername: outcomePayload.accountUsername,
                accountData: accountPayload,
                assignedCaller: outcomePayload.assignedCaller,
                comments: nextComments,
                timeline: nextTimeline,
                lockOwnerUserId: lockUserId,
                userId: lockUserId,
                currentUserId,
                updatedByUserId: currentUserId,
                updatedBy: current_comment_user_label(user)
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (updatedOffer?._id) {
                refresh_offer_in_board(updatedOffer);
                setSelectedSassOffer(null);
                return updatedOffer;
            }

            refresh_offer_in_board(offerItem);
            return {
                saveError: 'Board save failed.'
            };
        } catch (error) {
            refresh_offer_in_board(offerItem);
            return {
                saveError: board_save_error_message(error)
            };
        }
    };

    const cancel_sass_progress = async(offerItem = {}) => {
        const targetOfferId = offer_identity_value(offerItem);
        const nextStatus = 'set';
        const nextTimeline = timeline_without_active_in_progress(offerItem);
        const nextOffer = sass_offer_with_board_status(offerItem, nextStatus, nextTimeline);
        const currentUserId = current_user_id(user);
        const lockUserId = sass_lock_user_id(offerItem, user);

        if (!targetOfferId.length) {
            return false;
        }

        setOffers((previousOffers) => {
            return previousOffers.map((currentOffer) => {
                return offer_identity_value(currentOffer) === targetOfferId ? nextOffer : currentOffer;
            });
        });
        setSelectedSassOffer(null);

        try {
            const updateResponse = await update_sass_board_status_by_id(targetOfferId, {
                sassBoardStatus: nextStatus,
                timeline: nextTimeline,
                lockOwnerUserId: lockUserId,
                userId: lockUserId,
                currentUserId,
                updatedByUserId: currentUserId,
                updatedBy: current_comment_user_label(user)
            });
            const updatedOffer = updateResponse?.data?.data || {};

            if (updatedOffer?._id) {
                refresh_offer_in_board(updatedOffer);
                return updatedOffer;
            }

            window.alert(t('Board save failed.'));
            refresh_offer_in_board(offerItem);
            return false;
        } catch (error) {
            window.alert(t(board_save_error_message(error)));
            refresh_offer_in_board(offerItem);
            return false;
        }
    };

    const open_offer = (offerItem = {}) => {
        if (dragClickLockRef.current) {
            return;
        }

        setSelectedSassOffer(offerItem);
    };

    return (
        <PageSidebar>
            <Toolbar_
                sidebar={sidebar}
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                activeOption="sass"
            />
            <SassPageContent>
                <Sass_Brand_Slider
                    offerItem={selectedSassOffer}
                    accountData={sass_account_data(selectedSassOffer)}
                    assignedCallerOptions={teamMemberOptions}
                    categoryMap={nifflerCategoryMap}
                    timelineEvents={timelineEvents}
                    onRefreshTimeline={timelinePage.refresh}
                    timelineLoading={timelineLoading}
                    timelineLoadingMore={timelineLoadingMore}
                    timelineHasMore={timelineHasMore}
                    timelineError={timelineError}
                    timelineWarning={timelineWarning}
                    timelinePublication={timelinePublication}
                    boardStatus={sass_column_key(selectedSassOffer)}
                    canManageAssignments={currentUserCanManageAssignments}
                    inProgress={sass_column_key(selectedSassOffer) === 'in_progress'}
                    t={t}
                    onClose={() => setSelectedSassOffer(null)}
                    onSubmitComment={submit_sass_comment}
                    onLoadMoreTimeline={load_more_sass_timeline}
                    onRetryTimeline={retry_sass_timeline}
                    onSaveAccountData={save_sass_account_data}
                    onMatchAccountUsername={match_sass_account_username}
                    onLoadBusinessOptions={load_sass_business_options}
                    onSaveAssignedCaller={save_sass_assigned_caller}
                    onSubmitOutcome={submit_sass_outcome}
                    onCancelProgress={cancel_sass_progress}
                />
                {pendingSassReassignment ? (
                    <Confirmation_
                        message={t('Assign this SASS card to you and move it to IN PROGRESS?')}
                        no={t('No')}
                        yes={t('Yes')}
                        onNo={() => setPendingSassReassignment(null)}
                        onYes={confirm_sass_reassignment}
                    />
                ) : null}
                <PageTitle cta>
                    <SassBoardHeader>
                        <h1>{t('Sass')}</h1>
                    </SassBoardHeader>
                </PageTitle>
                <Sass_Actions
                    t={t}
                    searchValue={sassSearchInput}
                    onSearchChange={setSassSearchInput}
                    sellerOptions={sellerOptions}
                    selectedSellerKeys={selectedSassSellerKeys}
                    onSellerClick={toggle_sass_seller_filter}
                />
                {errorMessage.length ? <p>{errorMessage}</p> : null}
                {isLoading ? <p>{t('Loading board...')}</p> : null}
                <SassBoardStage>
                    <SassBoardColumns columnCount={sassBoardColumns.length}>
                        {sassBoardColumns.map((columnItem) => {
                            const columnOffers = offersByColumn[columnItem.key] || [];

                            return (
                                <SassBoardColumn
                                    key={columnItem.key}
                                    onDragOver={allow_offer_drop}
                                    onDrop={(event) => drop_offer_on_column(event, columnItem.key)}
                                >
                                    <SassBoardColumnHeader>
                                        <SassBoardColumnTitle>
                                            <h4>{t(columnItem.title)}</h4>
                                        </SassBoardColumnTitle>
                                        <SassBoardColumnCount>
                                            <h6>{columnOffers.length}</h6>
                                        </SassBoardColumnCount>
                                    </SassBoardColumnHeader>
                                    <SassBoardColumnBody>
                                        {!isLoading && !columnOffers.length ? (
                                            <SassBoardEmpty>
                                                <p>{t('No offers in this column.')}</p>
                                            </SassBoardEmpty>
                                        ) : null}
                                        {columnOffers.map((offerItem) => (
                                            <Sass_Offer_Card
                                                key={offer_identity_value(offerItem) || offer_reference(offerItem)}
                                                offerItem={offerItem}
                                                cardData={sass_card_data(offerItem)}
                                                canDrag={read_offer_drag_allowed(offerItem)}
                                                t={t}
                                                onOpen={open_offer}
                                                dragOfferId={dragOfferId}
                                                onDragHandleDown={mark_drag_handle_down}
                                                onDragStart={start_drag_offer}
                                                onDragEnd={finish_drag_offer}
                                                SellerAvatar={Sass_Seller_Avatar}
                                                canClaim={currentUserCanClaim && sass_column_key(offerItem) === 'set' && !sass_caller(offerItem)}
                                                onClaim={claim_unassigned_sass}
                                            />
                                        ))}
                                    </SassBoardColumnBody>
                                </SassBoardColumn>
                            );
                        })}
                    </SassBoardColumns>
                </SassBoardStage>
            </SassPageContent>
        </PageSidebar>
    );
}

const SassPageContent = styled(PageFull)`
    display: grid;
    gap: 1rem;
`;

const SassBoardHeader = styled.div`
    display: flex;
    align-items: center;
`;

const SassBoardActions = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 1rem;
`;

const SassBoardSearchTeam = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const SassBoardSearch = styled.div`
    min-width: 26rem;
    margin-bottom: var(--smallPads);

    input {
        padding-top: 1rem;
    }

    label {
        display: none;
    }
`;

const SassBoardTeam = styled.div`
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
`;

const SassBoardTeamMember = styled.div`
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0.2rem solid ${p => p.active ? 'var(--main)' : 'transparent'};
    cursor: pointer;
    overflow: hidden;

    > div {
        width: 100%;
        height: 100%;
    }
`;

const SassBoardStage = styled.div`
    position: relative;
    min-height: 42rem;
`;

const SassBoardColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(${p => p.columnCount}, minmax(22rem, 1fr));
    gap: 1.2rem;
    overflow-x: auto;
    padding-bottom: 1.5rem;
`;

const SassBoardColumn = styled(Card)`
    height: calc(100vh - 27rem);
    min-height: 42rem;
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    border: 0.2rem solid ${p => p.theme.mid};
    overflow: auto;
`;

const SassBoardColumnHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
`;

const SassBoardColumnTitle = styled.div`
    display: grid;
    gap: 0.2rem;

    h4,
    h6 {
        margin: 0;
    }
`;

const SassBoardColumnCount = styled.div`
    padding: 0.4rem 0.8rem;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};
`;

const SassBoardColumnBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const SassBoardEmpty = styled(Card)`
    padding: var(--normalPads);
    background: ${p => p.theme.low};
`;

const SassSellerAvatar = styled.div`
    width: ${p => p.size || '3.2rem'};
    height: ${p => p.size || '3.2rem'};
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${p => p.theme.mid};

    h6 {
        margin: 0;
        font-family: "fontBold";
    }
`;

const SassSellerAvatarImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
`;

const sassBoardColumns = [
    {
        key: 'set',
        title: 'SET'
    },
    {
        key: 'in_progress',
        title: 'IN PROGRESS'
    },
    {
        key: 'paid',
        title: 'PAID'
    }
];

const read_normalized_value = (value = '') => {
    return `${value || ''}`.trim().toLowerCase();
};

const read_sass_board_status_key = (value = '') => {
    const statusKey = read_normalized_value(value).replace(/[\s-]+/g, '_');
    const statusLookup = {
        meeting: 'in_progress',
        inprogress: 'in_progress',
        in_progress: 'in_progress',
        informer: 'paid',
        informed: 'paid',
        paid: 'paid',
        lost: 'lost',
        set: 'set'
    };

    return statusLookup[statusKey] || statusKey || 'set';
};

const sass_status_display_label = (value = '') => {
    const statusKey = read_sass_board_status_key(value);
    const labelLookup = {
        set: 'SET',
        in_progress: 'IN PROGRESS',
        paid: 'PAID',
        lost: 'LOST'
    };

    return labelLookup[statusKey] || value || 'SET';
};

const read_display_value = (value = '', fallback = '-') => {
    const cleanValue = `${value || ''}`.trim();

    return cleanValue.length ? cleanValue : fallback;
};

const read_first_display_value = (values = [], fallback = '-') => {
    const firstValue = values.map((valueItem) => `${valueItem || ''}`.trim()).find((valueItem) => valueItem.length);

    return firstValue || fallback;
};

const read_array_values = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (`${value || ''}`.trim().length) {
        return [value];
    }

    return [];
};

const read_page_assets_from_offer = (offerItem = {}) => {
    return read_array_values(offerItem?.niffler?.assets).filter((asset) => {
        return read_normalized_value(asset?.type) === 'page';
    });
};

const read_dailyprofit_business_assets_from_offer = (offerItem = {}) => {
    return read_array_values(offerItem?.niffler?.assets).filter((asset) => {
        const businessId = read_normalized_value(asset?.assetSourceId || asset?.businessId || asset?.metadata?.businessId);
        const businessName = read_normalized_value(asset?.name || asset?.businessName || asset?.metadata?.businessName);

        return read_normalized_value(asset?.type) === 'dailyprofit_business'
            && businessId.length
            && !['default', 'no-business'].includes(businessId)
            && !['default', 'no business'].includes(businessName);
    });
};

const read_selected_business_asset = (offerItem = {}) => {
    return read_dailyprofit_business_assets_from_offer(offerItem)[0] || {};
};

const read_ad_business_provider = (business = {}) => {
    const provider = read_normalized_value(
        business?.provider
        || business?.metadata?.provider
        || read_array_values(business?.assets).find((asset) => read_normalized_value(asset?.provider).length)?.provider
    ).replace(/[\s-]+/g, '_');
    const identityKey = read_normalized_value(business?.identityKey || business?.metadata?.identityKey);

    if (['google', 'google_ads'].includes(provider)
        || identityKey.startsWith('google:')
        || read_normalized_value(business?.googleCustomerId || business?.metadata?.googleCustomerId).length) {
        return 'google';
    }

    return 'meta';
};

const read_selected_ad_businesses_from_offer = (offerItem = {}) => {
    return read_dailyprofit_business_assets_from_offer(offerItem).map((asset) => {
        const metadata = asset?.metadata || {};
        const businessId = `${asset?.assetSourceId || asset?.businessId || metadata?.businessId || ''}`.trim();
        const businessName = `${asset?.name || asset?.businessName || metadata?.businessName || businessId}`.trim();

        return {
            ...metadata,
            ...asset,
            value: businessId,
            label: businessName,
            businessId,
            businessName,
            provider: read_ad_business_provider(asset)
        };
    });
};

const read_facebook_page_asset = (offerItem = {}) => {
    return read_page_assets_from_offer(offerItem)[0] || {};
};

const read_account_user_id_from_offer = (offerItem = {}) => {
    return read_first_display_value([
        offerItem?.niffler?.client?.accountUserId,
        offerItem?.niffler?.client?.userId,
        offerItem?.client?.accountUserId,
        offerItem?.client?.userId,
        offerItem?.typesData?.sourceClientUserId
    ], '');
};

const read_sass_linked_labels = (offerItem = {}) => {
    const nifflerBrandId = read_first_display_value([
        offerItem?.sourceBrandId,
        offerItem?.niffler?.sourceBrandId,
        offerItem?.connection?.links?.niffler?.brandId,
        offerItem?.connection?.connectionKey
    ], '');
    const cerebroClientId = read_first_display_value([
        offerItem?.connection?.links?.cerebro?.clientId,
        offerItem?.connection?.links?.cerebro?.mongoId,
        offerItem?.workflow?.cerebroConnection?.clientId,
        offerItem?.workflow?.cerebroConnection?.mongoId,
        offerItem?.niffler?.clientLink?.clientId,
        offerItem?.niffler?.client?.clientId
    ], '');

    return unique_display_values([
        nifflerBrandId.length ? 'Leads' : '',
        cerebroClientId.length ? 'Tasks' : ''
    ]);
};

const read_sass_card_linked_labels = (offerItem = {}) => {
    return read_sass_linked_labels(offerItem).filter((linkedLabel) => linkedLabel !== 'Leads');
};

const read_sass_connection_status = (offerItem = {}) => {
    return read_sass_workflow_status(offerItem).label;
};

const read_object_value = (value) => {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};

const read_sass_categories_from_response = (response = {}) => {
    const responseData = response?.data || {};
    const payload = responseData?.data || responseData || {};

    return payload?.categories || payload?.data || {};
};

const unique_display_values = (values = []) => {
    const seenValues = new Set();

    return values.map((valueItem) => `${valueItem || ''}`.trim()).filter((valueItem) => {
        const valueKey = valueItem.toLowerCase();
        const keepValue = valueItem.length && !seenValues.has(valueKey);

        if (keepValue) {
            seenValues.add(valueKey);
        }

        return keepValue;
    });
};

const offer_identity_value = (offerItem = {}) => {
    return read_sass_offer_registration_id(offerItem);
};

const offer_reference = (offerItem = {}) => {
    return offer_reference_from_offer(offerItem);
};

const offer_brand = (offerItem = {}) => {
    return offer_client_name_from_offer(offerItem);
};

const offer_email = (offerItem = {}) => {
    const emails = offer_emails_from_offer(offerItem);

    return emails[0] || '';
};

const selected_offer_types = (offerItem = {}) => {
    return selected_types_from_offer(offerItem);
};

const sass_import_payload = (offerItem = {}) => {
    return offerItem?.typesData?.sassImportPayload || offerItem?.sassImportPayload || {};
};

const sass_brand_payload = (offerItem = {}) => {
    const importPayload = sass_import_payload(offerItem);

    return importPayload?.sass || importPayload?.brand || offerItem?.sass || {};
};

const sass_lead_payload = (offerItem = {}) => {
    const importPayload = sass_import_payload(offerItem);

    return importPayload?.lead || offerItem?.lead || offerItem?.niffler?.lead || {};
};

const sass_sales_seller = (offerItem = {}) => {
    const brandPayload = sass_brand_payload(offerItem);
    const leadPayload = sass_lead_payload(offerItem);

    return offerItem?.niffler?.seller
        || brandPayload?.seller
        || leadPayload?.seller
        || offerItem?.seller
        || null;
};

const sass_caller = (offerItem = {}) => {
    if (offerItem?.sassAssignee !== undefined) {
        return offerItem.sassAssignee || null;
    }

    const brandPayload = sass_brand_payload(offerItem);
    const leadPayload = sass_lead_payload(offerItem);

    return offerItem?.setupData?.sassSeller
        || offerItem?.typesData?.sassCaller
        || offerItem?.typesData?.sassSeller
        || offerItem?.setupData?.sassCaller
        || offerItem?.niffler?.caller
        || brandPayload?.caller
        || leadPayload?.caller
        || brandPayload?.contractSeller
        || leadPayload?.contractSeller
        || offerItem?.caller
        || null;
};

const sass_seller = (offerItem = {}) => {
    if (offerItem?.sassAssignee !== undefined) {
        return offerItem.sassAssignee || null;
    }

    return sass_caller(offerItem) || sass_sales_seller(offerItem);
};

const seller_label = (seller = null) => {
    if (typeof seller === 'string') {
        return read_display_value(seller);
    }

    return read_first_display_value([
        seller?.name,
        seller?.fullName,
        seller?.displayName,
        seller?.optionLabel,
        seller?.label,
        `${seller?.firstName || seller?.firstname || ''} ${seller?.lastName || seller?.lastname || ''}`.trim(),
        seller?.username,
        seller?.email,
        seller?.userId,
        seller?.optionValue
    ]);
};

const read_member_identifier = (memberItem = {}) => {
    return String(
        memberItem?.email
        || memberItem?.memberEmail
        || memberItem?.username
        || memberItem?.memberUsername
        || memberItem?.userName
        || memberItem?.preferred_username
        || memberItem?.userId
        || memberItem?.subId
        || memberItem?.id
        || memberItem?._id
        || memberItem?.sub
        || memberItem?.name
        || memberItem?.fullName
        || memberItem?.value
        || memberItem?.optionValue
        || memberItem?.label
        || memberItem?.optionLabel
        || ''
    ).trim();
};

const seller_avatar_url = (seller = null) => {
    if (!seller || typeof seller === 'string') {
        return '';
    }

    const userId = read_first_display_value([
        seller?.sub,
        seller?.subId,
        seller?.userId,
        seller?.keycloakId,
        seller?.authUserId
    ], '');

    if (!userId.length) {
        return '';
    }

    return `${s3}erisedev/${userId}/avatar.png`;
};

const seller_initials = (seller = null) => {
    const label = seller_label(seller).split('@')[0].replace(/[._-]+/g, ' ').trim();
    const labelParts = label.split(/\s+/).filter(Boolean);
    const firstInitial = `${labelParts[0] || ''}`.charAt(0).toUpperCase();
    const secondInitial = `${labelParts[1] || ''}`.charAt(0).toUpperCase();
    const initials = `${firstInitial}${secondInitial}`;

    return initials || '--';
};

const current_user_id = (user = null) => {
    return read_first_display_value([
        user?.userId,
        user?.subId,
        user?.sub,
        user?.id,
        user?._id
    ], '');
};

const read_sass_assignment_admin = (user = {}) => {
    const roleValues = [
        ...(Array.isArray(user?.roles) ? user.roles : []),
        ...Object.values(user?.resource_access || {}).flatMap((resource) => Array.isArray(resource?.roles) ? resource.roles : [])
    ];

    return roleValues.some((role) => {
        const roleValue = read_normalized_value(role?.name || role?.role || role);
        return roleValue === 'king' || roleValue === 'admin';
    });
};

const explicit_seller_user_id = (seller = null) => {
    return `${seller?.userId || ''}`.trim();
};

const sass_lock_user_id = (offerItem = {}, user = null) => {
    return read_first_display_value([
        explicit_seller_user_id(sass_caller(offerItem)),
        explicit_seller_user_id(sass_seller(offerItem)),
        current_user_id(user)
    ], '');
};

const Sass_Seller_Avatar = (props) => {
    const avatarUrl = props.showImage === false ? '' : seller_avatar_url(props.seller);
    const [imageVisible, setImageVisible] = useState(Boolean(avatarUrl));

    useEffect(() => {
        setImageVisible(Boolean(avatarUrl));
    }, [avatarUrl]);

    return (
        <SassSellerAvatar title={seller_label(props.seller)} size={props.size}>
            {imageVisible ? (
                <SassSellerAvatarImage
                    src={avatarUrl}
                    alt={seller_label(props.seller)}
                    onError={() => setImageVisible(false)}
                />
            ) : (
                <h6>{seller_initials(props.seller)}</h6>
            )}
        </SassSellerAvatar>
    );
};

const sass_meeting_date = (offerItem = {}) => {
    const brandPayload = sass_brand_payload(offerItem);
    const leadPayload = sass_lead_payload(offerItem);

    return read_first_display_value([
        offerItem?.typesData?.sassMeetingDate,
        offerItem?.setupData?.sassMeetingDate,
        brandPayload?.meetingDate,
        leadPayload?.meetingDate,
        sass_import_payload(offerItem)?.action?.to?.meetingDate
    ], '');
};

const sass_status_label = (offerItem = {}) => {
    const brandPayload = sass_brand_payload(offerItem);

    const statusValue = read_first_display_value([
        offerItem?.typesData?.sassBoardStatus,
        offerItem?.setupData?.sassBoardStatus,
        brandPayload?.prospectStatus,
        offer_current_status(offerItem),
        offerItem?.status
    ], 'set');

    return sass_status_display_label(statusValue);
};

const sass_column_from_status_values = (statusValues = []) => {
    const statusKeys = statusValues.map((statusValue) => {
        return read_sass_board_status_key(statusValue);
    });

    if (statusKeys.includes('lost')) {
        return 'lost';
    }

    if (statusKeys.includes('paid')) {
        return 'paid';
    }

    if (statusKeys.includes('in_progress')) {
        return 'in_progress';
    }

    return 'set';
};

const sass_board_status_key = (offerItem = {}) => {
    const boardStatusValues = [
        offerItem?.typesData?.sassBoardStatus,
        offerItem?.setupData?.sassBoardStatus
    ].filter((statusValue) => {
        return read_normalized_value(statusValue).length;
    });

    if (!boardStatusValues.length) {
        return '';
    }

    return sass_column_from_status_values(boardStatusValues);
};

const sass_status_key = (offerItem = {}) => {
    const brandPayload = sass_brand_payload(offerItem);
    const boardStatusKey = sass_board_status_key(offerItem);

    if (boardStatusKey.length) {
        return boardStatusKey;
    }

    return sass_column_from_status_values([
        brandPayload?.prospectStatus,
        offer_current_status(offerItem),
        offerItem?.status
    ]);
};

const sass_column_key = (offerItem = {}) => {
    return sass_status_key(offerItem);
};

const offer_is_sass = (offerItem = {}) => {
    return selected_offer_types(offerItem).includes('sass');
};

const sorted_board_offers = (offers = []) => {
    return [...offers].sort((leftItem, rightItem) => {
        const leftTime = new Date(leftItem?.updatedAt || leftItem?.createdAt || 0).getTime();
        const rightTime = new Date(rightItem?.updatedAt || rightItem?.createdAt || 0).getTime();

        return rightTime - leftTime;
    });
};

const meeting_day_start_time = (dateValue = '') => {
    const parsedDate = new Date(dateValue || '');

    if (Number.isNaN(parsedDate.getTime())) {
        return 0;
    }

    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()).getTime();
};

const sass_meeting_day_distance = (offerItem = {}) => {
    const meetingStartTime = meeting_day_start_time(sass_meeting_date(offerItem));

    if (!meetingStartTime) {
        return Number.MAX_SAFE_INTEGER;
    }

    const todayStartTime = meeting_day_start_time(new Date());
    const oneDayTime = 24 * 60 * 60 * 1000;

    return Math.floor((meetingStartTime - todayStartTime) / oneDayTime);
};

const sass_meeting_tone = (offerItem = {}) => {
    const dayDistance = sass_meeting_day_distance(offerItem);

    if (dayDistance <= 0) {
        return 'error';
    }

    if (dayDistance === 1) {
        return 'warning';
    }

    return 'success';
};

const sorted_set_board_offers = (offers = []) => {
    return [...offers].sort((leftItem, rightItem) => {
        const leftDistance = sass_meeting_day_distance(leftItem);
        const rightDistance = sass_meeting_day_distance(rightItem);

        if (leftDistance !== rightDistance) {
            return leftDistance - rightDistance;
        }

        const leftTime = new Date(leftItem?.updatedAt || leftItem?.createdAt || 0).getTime();
        const rightTime = new Date(rightItem?.updatedAt || rightItem?.createdAt || 0).getTime();

        return rightTime - leftTime;
    });
};

const sorted_column_offers = (offers = [], columnKey = '') => {
    if (columnKey === 'set') {
        return sorted_set_board_offers(offers);
    }

    return sorted_board_offers(offers);
};

const offers_by_column = (offers = []) => {
    return sassBoardColumns.reduce((accumulator, columnItem) => {
        return {
            ...accumulator,
            [columnItem.key]: sorted_column_offers(offers.filter((offerItem) => {
                return sass_column_key(offerItem) === columnItem.key;
            }), columnItem.key)
        };
    }, {});
};

const sass_seller_filter_key = (seller = null) => {
    const label = seller_label(seller);

    if (label === '-') {
        return '';
    }

    return read_normalized_value(label);
};

const seller_avatar_score = (seller = null) => {
    return seller_avatar_url(seller).length ? 1 : 0;
};

const preferred_seller_for_filter = (currentSeller = null, nextSeller = null) => {
    const currentScore = seller_avatar_score(currentSeller);
    const nextScore = seller_avatar_score(nextSeller);

    if (nextScore > currentScore) {
        return nextSeller;
    }

    return currentSeller || nextSeller;
};

const sass_board_seller_options = (offers = []) => {
    const sellerLookup = {};

    offers.forEach((offerItem) => {
        const seller = sass_seller(offerItem);
        const sellerKey = sass_seller_filter_key(seller);

        if (!sellerKey) {
            return;
        }

        sellerLookup[sellerKey] = {
            key: sellerKey,
            label: seller_label(seller),
            seller: preferred_seller_for_filter(sellerLookup[sellerKey]?.seller, seller)
        };
    });

    return Object.values(sellerLookup).sort((leftItem, rightItem) => {
        return leftItem.label.localeCompare(rightItem.label);
    });
};

const sass_offer_matches_search = (offerItem = {}, searchValue = '') => {
    return object_matches_search(offerItem, searchValue);
};

const sass_offer_matches_seller = (offerItem = {}, selectedSellerKeys = []) => {
    if (!selectedSellerKeys.length) {
        return true;
    }

    return selectedSellerKeys.includes(sass_seller_filter_key(sass_seller(offerItem)));
};

const filtered_sass_board_offers = (offers = [], filters = {}) => {
    return offers.filter((offerItem) => {
        return sass_offer_matches_search(offerItem, filters.searchValue)
            && sass_offer_matches_seller(offerItem, filters.selectedSellerKeys || []);
    });
};

const read_sass_outcome_board_status = (outcome = '') => {
    const outcomeKey = read_sass_board_status_key(outcome);
    const outcomeLookup = {
        rescheduled: 'set',
        paid: 'paid',
        lost: 'lost'
    };

    return outcomeLookup[outcomeKey] || '';
};

const html_to_plain_text = (value = '') => {
    return `${value || ''}`
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const read_sass_outcome_note = (outcomePayload = {}) => {
    const outcomeKey = read_normalized_value(outcomePayload?.outcome).replace(/[\s-]+/g, '_');

    if (outcomeKey === 'rescheduled') {
        return `Rescheduled for ${format_date_time(outcomePayload?.rescheduledAt)}`;
    }

    if (outcomeKey === 'paid') {
        return `Paid by ${outcomePayload?.accountUsername || '-'}`;
    }

    if (outcomeKey === 'lost') {
        return html_to_plain_text(outcomePayload?.comment);
    }

    return '';
};

const sass_comments_for_outcome = (offerItem = {}, outcomePayload = {}, user = null) => {
    const currentComments = read_array_values(offerItem?.comments);
    const outcomeKey = read_normalized_value(outcomePayload?.outcome).replace(/[\s-]+/g, '_');
    const commentBody = `${outcomePayload?.comment || ''}`.trim();

    if (outcomeKey !== 'lost' || !html_to_plain_text(commentBody).length) {
        return currentComments;
    }

    return [
        ...currentComments,
        {
            id: `comment_${Date.now()}`,
            body: commentBody,
            html: commentBody,
            createdAt: new Date().toISOString(),
            createdBy: current_comment_user_label(user),
            createdByUserId: `${user?.subId || user?.sub || user?.userId || user?.id || user?._id || ''}`,
            createdByEmail: `${user?.email || user?.preferred_username || ''}`,
            createdByUsername: `${user?.username || user?.preferred_username || ''}`
        }
    ];
};

const clean_account_list_values = (values = []) => {
    return unique_display_values(read_array_values(values).map((value) => `${value || ''}`.trim()));
};

const sass_assets_with_selected_business = (offerItem = {}, selectedBusinesses = [], accountUserId = '', accountUsername = '') => {
    const currentAssets = read_array_values(offerItem?.niffler?.assets).filter((asset) => {
        return read_normalized_value(asset?.type) !== 'dailyprofit_business';
    });
    const businessAssets = read_array_values(selectedBusinesses).map((selectedBusiness) => {
        const selectedBusinessId = `${selectedBusiness?.value || selectedBusiness?.businessId || selectedBusiness?._id || selectedBusiness?.id || ''}`.trim();
        const selectedBusinessLabel = `${selectedBusiness?.label || selectedBusiness?.businessName || selectedBusiness?.name || ''}`.trim();

        return {
            ...selectedBusiness,
            userId: accountUserId,
            type: 'dailyprofit_business',
            assetSourceId: selectedBusinessId,
            name: selectedBusinessLabel,
            source: 'dailyprofit',
            provider: read_ad_business_provider(selectedBusiness),
            metadata: {
                ...(selectedBusiness?.metadata || {}),
                userId: accountUserId,
                username: accountUsername,
                businessId: selectedBusinessId,
                businessName: selectedBusinessLabel,
                provider: read_ad_business_provider(selectedBusiness),
                identityKey: `${selectedBusiness?.identityKey || selectedBusiness?.metadata?.identityKey || ''}`.trim(),
                facebookPageId: `${selectedBusiness?.facebookPageId || selectedBusiness?.metadata?.facebookPageId || ''}`.trim(),
                facebookPageName: `${selectedBusiness?.facebookPageName || selectedBusiness?.metadata?.facebookPageName || ''}`.trim(),
                googleCustomerId: `${selectedBusiness?.googleCustomerId || selectedBusiness?.metadata?.googleCustomerId || ''}`.trim(),
                googleAccountName: `${selectedBusiness?.googleAccountName || selectedBusiness?.metadata?.googleAccountName || ''}`.trim(),
                image: `${selectedBusiness?.image || selectedBusiness?.metadata?.image || ''}`.trim()
            }
        };
    }).filter((asset) => {
        return asset.assetSourceId.length
            && !['default', 'no-business'].includes(asset.assetSourceId.toLowerCase())
            && !['default', 'no business'].includes(asset.name.toLowerCase());
    });

    return businessAssets.length ? [...currentAssets, ...businessAssets] : currentAssets;
};

const sass_offer_with_account_data = (offerItem = {}, nextStatus = '', accountPayload = {}) => {
    const phoneNumbers = clean_account_list_values(accountPayload.phoneNumbers);
    const emailAddresses = clean_account_list_values(accountPayload.emailAddresses);
    const brandName = `${accountPayload?.brandName || ''}`.trim();
    const firstName = `${accountPayload?.firstName || ''}`.trim();
    const lastName = `${accountPayload?.lastName || ''}`.trim();
    const accountUsername = `${accountPayload?.accountUsername || ''}`.trim();
    const accountUserId = `${accountPayload?.accountUserId || accountPayload?.userId || ''}`.trim();
    const category = `${accountPayload?.category || ''}`.trim();
    const subcategory = `${accountPayload?.subcategory || ''}`.trim();
    const location = `${accountPayload?.location || ''}`.trim();
    const selectedBusinesses = Array.isArray(accountPayload?.selectedAdBusinesses)
        ? accountPayload.selectedAdBusinesses
        : [read_object_value(accountPayload?.selectedBusiness)];
    const nextAssets = sass_assets_with_selected_business(offerItem, selectedBusinesses, accountUserId, accountUsername);

    return {
        ...offerItem,
        clientName: brandName,
        clientEmails: emailAddresses,
        clientHeadquarters: location,
        client: {
            ...(offerItem?.client || {}),
            brand: brandName,
            brandName,
            name: brandName,
            emails: emailAddresses,
            emailAddresses,
            email: emailAddresses[0] || '',
            numbers: phoneNumbers,
            phoneNumbers,
            phones: phoneNumbers,
            phone: phoneNumbers[0] || '',
            ownerFirstname: firstName,
            ownerFirstName: firstName,
            firstName,
            ownerLastname: lastName,
            ownerLastName: lastName,
            lastName,
            username: accountUsername,
            ownerUsername: accountUsername,
            accountUserId,
            userId: accountUserId,
            category,
            subcategory,
            location,
            headquarters: location,
            sassAccountEdited: true
        },
        setupData: {
            ...(offerItem?.setupData || {}),
            sassBoardStatus: nextStatus,
            clientName: brandName,
            clientEmails: emailAddresses,
            contactPhone: phoneNumbers[0] || '',
            contactPhones: phoneNumbers,
            category,
            subcategory,
            clientHeadquarters: location
        },
        typesData: {
            ...(offerItem?.typesData || {}),
            sassBoardStatus: nextStatus,
            clientName: brandName,
            sourceBrandName: brandName,
            clientEmails: emailAddresses,
            contactPhone: phoneNumbers[0] || '',
            contactPhones: phoneNumbers,
            sourceClientFirstname: firstName,
            sourceClientLastname: lastName,
            sourceClientUsername: accountUsername,
            sourceClientUserId: accountUserId,
            sourceClientCategory: category,
            sourceClientSubcategory: subcategory,
            sourceClientLocation: location,
            category,
            subcategory,
            clientHeadquarters: location
        },
        niffler: {
            ...(offerItem?.niffler || {}),
            sourceBrandName: brandName,
            assets: nextAssets,
            client: {
                ...(offerItem?.niffler?.client || {}),
                brand: brandName,
                brandName,
                name: brandName,
                ownerFirstName: firstName,
                ownerFirstname: firstName,
                firstName,
                ownerLastName: lastName,
                ownerLastname: lastName,
                lastName,
                username: accountUsername,
                ownerUsername: accountUsername,
                accountUserId,
                userId: accountUserId,
                category,
                subcategory,
                location,
                headquarters: location,
                phoneNumbers,
                phones: phoneNumbers,
                phone: phoneNumbers[0] || '',
                emailAddresses,
                emails: emailAddresses,
                email: emailAddresses[0] || '',
                sassAccountEdited: true
            }
        }
    };
};

const sass_offer_with_outcome = (offerItem = {}, nextStatus = '', outcomePayload = {}, nextTimeline = [], nextComments = []) => {
    const nextMeetingDate = `${outcomePayload?.rescheduledAt || ''}`.trim();
    const nextUsername = `${outcomePayload?.accountUsername || ''}`.trim();
    const nextAssignedCaller = read_object_value(outcomePayload?.assignedCaller);
    const hasAssignedCaller = Object.keys(nextAssignedCaller).length > 0;
    const offerWithMeetingDate = read_sass_offer_with_meeting_date(offerItem, nextMeetingDate);

    return {
        ...offerWithMeetingDate,
        comments: nextComments,
        timeline: nextTimeline,
        setupData: {
            ...(offerWithMeetingDate?.setupData || {}),
            sassBoardStatus: nextStatus,
            ...(hasAssignedCaller ? { sassSeller: nextAssignedCaller, sassCaller: nextAssignedCaller } : {})
        },
        typesData: {
            ...(offerWithMeetingDate?.typesData || {}),
            sassBoardStatus: nextStatus,
            sassTimeline: nextTimeline,
            ...(hasAssignedCaller ? { sassSeller: nextAssignedCaller, sassCaller: nextAssignedCaller } : {}),
            ...(nextUsername.length ? { sourceClientUsername: nextUsername } : {})
        },
        niffler: {
            ...(offerWithMeetingDate?.niffler || {}),
            ...(hasAssignedCaller ? { caller: nextAssignedCaller } : {}),
            client: {
                ...(offerWithMeetingDate?.niffler?.client || {}),
                ...(nextUsername.length ? { username: nextUsername } : {})
            },
            timeline: nextTimeline
        }
    };
};

const sass_offer_with_assigned_caller = (offerItem = {}, nextStatus = '', assignedCaller = {}) => {
    const nextAssignedCaller = read_object_value(assignedCaller);

    return {
        ...offerItem,
        setupData: {
            ...(offerItem?.setupData || {}),
            sassBoardStatus: nextStatus,
            sassSeller: nextAssignedCaller,
            sassCaller: nextAssignedCaller
        },
        typesData: {
            ...(offerItem?.typesData || {}),
            sassBoardStatus: nextStatus,
            sassSeller: nextAssignedCaller,
            sassCaller: nextAssignedCaller
        },
        niffler: {
            ...(offerItem?.niffler || {}),
            caller: nextAssignedCaller
        }
    };
};

const sass_offer_with_board_status = (offerItem = {}, nextStatus = '', nextTimeline = []) => {
    return {
        ...offerItem,
        timeline: nextTimeline,
        setupData: {
            ...(offerItem?.setupData || {}),
            sassBoardStatus: nextStatus
        },
        typesData: {
            ...(offerItem?.typesData || {}),
            sassBoardStatus: nextStatus,
            sassTimeline: nextTimeline
        },
        niffler: {
            ...(offerItem?.niffler || {}),
            timeline: nextTimeline
        }
    };
};

const timeline_without_active_in_progress = (offerItem = {}) => {
    const currentTimeline = Array.isArray(offerItem?.timeline) ? offerItem.timeline : [];
    const lastTimelineItem = currentTimeline[currentTimeline.length - 1] || {};
    const lastTimelineStatus = read_sass_board_status_key(lastTimelineItem?.status || '');

    if (lastTimelineStatus !== 'in_progress') {
        return currentTimeline;
    }

    return currentTimeline.slice(0, -1);
};

const timeline_for_drag_status = (offerItem = {}, nextStatus = '', note = '', seller = null) => {
    const normalizedStatus = read_sass_board_status_key(nextStatus);
    const now = new Date().toISOString();
    const currentTimeline = Array.isArray(offerItem?.timeline) ? offerItem.timeline : [];
    const normalizedTimeline = currentTimeline
        .map((timelineItem = {}) => {
            const timelineNote = `${timelineItem?.note || timelineItem?.description || timelineItem?.message || ''}`.trim();
            const normalizedTimelineItem = {
                status: read_sass_board_status_key(timelineItem?.status || ''),
                at: `${timelineItem?.at || timelineItem?.occurredAt || timelineItem?.createdAt || timelineItem?.updatedAt || ''}`.trim()
            };

            if (timelineNote.length) {
                normalizedTimelineItem.note = timelineNote;
            }

            if (timelineItem?.seller) {
                normalizedTimelineItem.seller = timelineItem.seller;
            }

            return normalizedTimelineItem;
        })
        .filter((timelineItem) => timelineItem?.status.length && timelineItem?.at.length);
    const currentLastStatus = read_sass_board_status_key(normalizedTimeline[normalizedTimeline.length - 1]?.status || offerItem?.status || '');

    if (!normalizedStatus.length || normalizedStatus === currentLastStatus) {
        return normalizedTimeline;
    }

    const nextTimelineItem = {
        status: normalizedStatus,
        at: now
    };

    if (`${note || ''}`.trim().length) {
        nextTimelineItem.note = `${note || ''}`.trim();
    }

    if (seller) {
        nextTimelineItem.seller = seller;
    }

    return [
        ...normalizedTimeline,
        nextTimelineItem
    ];
};

const board_save_error_message = (error = {}) => {
    return error?.response?.data?.message
        || error?.response?.data?.msg
        || 'Board save failed.';
};

const format_date_time = (dateValue = '') => {
    const cleanValue = `${dateValue || ''}`.trim();
    const parsedDate = new Date(cleanValue);

    if (!cleanValue.length || Number.isNaN(parsedDate.getTime())) {
        return cleanValue || '-';
    }

    const dateLabel = parsedDate.toLocaleDateString();
    const timeLabel = parsedDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    return `${dateLabel} at ${timeLabel}`;
};

const sass_card_data = (offerItem = {}) => {
    const seller = sass_sales_seller(offerItem);
    const caller = sass_caller(offerItem) || sass_seller(offerItem);
    const offerReference = offer_reference(offerItem);
    const workflowStatus = read_sass_workflow_status(offerItem);

    return {
        offerId: offer_identity_value(offerItem),
        offerReference,
        seller,
        caller,
        sellerLabel: seller_label(seller),
        callerLabel: seller_label(caller),
        statusLabel: sass_status_label(offerItem),
        brand: offer_brand(offerItem),
        meetingDateLabel: format_date_time(sass_meeting_date(offerItem)),
        meetingTone: sass_meeting_tone(offerItem),
        linkedLabels: read_sass_card_linked_labels(offerItem),
        syncStatusLabel: workflowStatus.label,
        syncStatusTone: workflowStatus.tone,
        footerCodes: unique_display_values([offerReference])
    };
};

const account_is_unregistered = (username = '', registrationStatus = '') => {
    const usernameKey = read_normalized_value(username);
    const statusKey = read_normalized_value(registrationStatus);

    return !usernameKey.length
        || usernameKey === 'unregistered'
        || statusKey === 'unregistered';
};

const sass_account_data = (offerItem = {}) => {
    const brandPayload = sass_brand_payload(offerItem);
    const leadPayload = sass_lead_payload(offerItem);
    const nifflerClient = offerItem?.niffler?.client || {};
    const typesData = offerItem?.typesData || {};
    const client = offerItem?.client || {};
    const accountDataEdited = nifflerClient?.sassAccountEdited === true || client?.sassAccountEdited === true;
    const seller = sass_sales_seller(offerItem);
    const caller = sass_caller(offerItem) || sass_seller(offerItem);
    const selectedBusinessAsset = read_selected_business_asset(offerItem);
    const facebookPageAsset = read_facebook_page_asset(offerItem);
    const facebookPageName = read_first_display_value([
        facebookPageAsset?.name,
        facebookPageAsset?.metadata?.facebookPageName
    ], '');
    const selectedBusinessValue = read_first_display_value([
        selectedBusinessAsset?.assetSourceId,
        selectedBusinessAsset?._id
    ], 'no-business');
    const selectedBusinessLabel = read_first_display_value([
        selectedBusinessAsset?.name,
        selectedBusinessAsset?.metadata?.businessName
    ], 'Select a business');
    const selectedAdBusinesses = read_selected_ad_businesses_from_offer(offerItem);
    const brandName = read_first_display_value(accountDataEdited ? [
        client?.brand,
        client?.brandName,
        client?.internalName,
        nifflerClient?.brand,
        nifflerClient?.brandName,
        nifflerClient?.internalName,
        offerItem?.niffler?.sourceBrandName,
        typesData?.sourceBrandName,
        typesData?.clientName,
        offerItem?.clientName,
        offerItem?.setupData?.clientName
    ] : [
        client?.brand,
        client?.brandName,
        client?.internalName,
        nifflerClient?.brand,
        nifflerClient?.brandName,
        nifflerClient?.internalName,
        offerItem?.niffler?.sourceBrandName,
        typesData?.sourceBrandName,
        typesData?.clientName,
        offerItem?.clientName,
        offerItem?.setupData?.clientName,
        brandPayload?.brandName,
        brandPayload?.brand,
        brandPayload?.internalName,
        leadPayload?.brandName,
        leadPayload?.brand,
        leadPayload?.internalName
    ], '');
    const firstName = read_first_display_value(accountDataEdited ? [
        nifflerClient?.ownerFirstName,
        nifflerClient?.ownerFirstname,
        nifflerClient?.firstName,
        nifflerClient?.firstname,
        client?.ownerFirstName,
        client?.ownerFirstname,
        client?.firstName,
        client?.firstname,
        typesData?.sourceClientFirstname
    ] : [
        nifflerClient?.ownerFirstName,
        nifflerClient?.ownerFirstname,
        nifflerClient?.firstName,
        nifflerClient?.firstname,
        brandPayload?.ownerFirstName,
        brandPayload?.ownerFirstname,
        leadPayload?.firstname,
        leadPayload?.firstName,
        client?.ownerFirstName,
        client?.ownerFirstname,
        client?.firstName,
        client?.firstname,
        typesData?.sourceClientFirstname
    ], '');
    const lastName = read_first_display_value(accountDataEdited ? [
        nifflerClient?.ownerLastName,
        nifflerClient?.ownerLastname,
        nifflerClient?.lastName,
        nifflerClient?.lastname,
        client?.ownerLastName,
        client?.ownerLastname,
        client?.lastName,
        client?.lastname,
        typesData?.sourceClientLastname
    ] : [
        nifflerClient?.ownerLastName,
        nifflerClient?.ownerLastname,
        nifflerClient?.lastName,
        nifflerClient?.lastname,
        brandPayload?.ownerLastName,
        brandPayload?.ownerLastname,
        leadPayload?.lastname,
        leadPayload?.lastName,
        client?.ownerLastName,
        client?.ownerLastname,
        client?.lastName,
        client?.lastname,
        typesData?.sourceClientLastname
    ], '');
    const username = read_first_display_value(accountDataEdited ? [
        nifflerClient?.username,
        nifflerClient?.ownerUsername,
        client?.username,
        client?.ownerUsername,
        typesData?.sourceClientUsername
    ] : [
        nifflerClient?.username,
        nifflerClient?.ownerUsername,
        client?.username,
        client?.ownerUsername,
        brandPayload?.ownerUsername,
        leadPayload?.ownerUsername,
        leadPayload?.username,
        typesData?.sourceClientUsername
    ], '');
    const registrationStatus = read_first_display_value([
        nifflerClient?.registrationStatus,
        client?.registrationStatus,
        brandPayload?.registrationStatus,
        leadPayload?.registrationStatus,
        typesData?.sourceClientRegistrationStatus
    ], '');
    const accountUserId = read_account_user_id_from_offer(offerItem);
    const userIsUnregistered = account_is_unregistered(username, registrationStatus);
    const category = read_first_display_value(accountDataEdited ? [
        nifflerClient?.category,
        nifflerClient?.Category,
        client?.category,
        client?.Category,
        typesData?.category,
        typesData?.sourceClientCategory
    ] : [
        nifflerClient?.category,
        nifflerClient?.Category,
        client?.category,
        client?.Category,
        typesData?.category,
        typesData?.sourceClientCategory,
        brandPayload?.category,
        leadPayload?.category
    ], '');
    const subcategory = read_first_display_value(accountDataEdited ? [
        nifflerClient?.subcategory,
        nifflerClient?.Subcategory,
        nifflerClient?.subCategory,
        client?.subcategory,
        client?.Subcategory,
        client?.subCategory,
        typesData?.subcategory,
        typesData?.Subcategory,
        typesData?.sourceClientSubcategory
    ] : [
        nifflerClient?.subcategory,
        nifflerClient?.Subcategory,
        nifflerClient?.subCategory,
        client?.subcategory,
        client?.Subcategory,
        client?.subCategory,
        typesData?.subcategory,
        typesData?.Subcategory,
        typesData?.sourceClientSubcategory,
        brandPayload?.subcategory,
        brandPayload?.Subcategory,
        leadPayload?.subcategory
    ], '');
    const location = read_first_display_value(accountDataEdited ? [
        nifflerClient?.location,
        nifflerClient?.headquarters,
        nifflerClient?.address,
        client?.location,
        client?.headquarters,
        client?.address,
        offerItem?.clientHeadquarters,
        typesData?.clientHeadquarters,
        typesData?.sourceClientLocation
    ] : [
        nifflerClient?.location,
        nifflerClient?.headquarters,
        nifflerClient?.address,
        client?.location,
        client?.headquarters,
        client?.address,
        offerItem?.clientHeadquarters,
        typesData?.clientHeadquarters,
        typesData?.sourceClientLocation,
        brandPayload?.location
    ], '');
    const phoneNumbers = unique_display_values(accountDataEdited ? [
        ...read_array_values(nifflerClient?.phoneNumbers),
        ...read_array_values(nifflerClient?.phones),
        ...read_array_values(nifflerClient?.phone),
        ...read_array_values(client?.phoneNumbers),
        ...read_array_values(client?.phones),
        ...read_array_values(client?.phone),
        ...read_array_values(offerItem?.clientPhones),
        ...read_array_values(typesData?.contactPhones),
        typesData?.contactPhone
    ] : [
        ...offer_numbers_from_offer(offerItem),
        ...read_array_values(client?.phoneNumbers),
        ...read_array_values(client?.phones),
        ...read_array_values(nifflerClient?.phoneNumbers),
        ...read_array_values(nifflerClient?.phones),
        ...read_array_values(nifflerClient?.phone),
        ...read_array_values(brandPayload?.phoneNumbers),
        ...read_array_values(brandPayload?.phones),
        ...read_array_values(brandPayload?.phone),
        ...read_array_values(leadPayload?.phones),
        ...read_array_values(leadPayload?.phoneNumbers)
    ]);
    const emailAddresses = unique_display_values(accountDataEdited ? [
        ...read_array_values(offerItem?.clientEmails),
        ...read_array_values(client?.emailAddresses),
        ...read_array_values(client?.emails),
        ...read_array_values(nifflerClient?.emailAddresses),
        ...read_array_values(nifflerClient?.emails),
        ...read_array_values(nifflerClient?.email),
        ...read_array_values(typesData?.clientEmails)
    ] : [
        ...offer_emails_from_offer(offerItem),
        ...read_array_values(client?.emailAddresses),
        ...read_array_values(client?.emails),
        ...read_array_values(nifflerClient?.emailAddresses),
        ...read_array_values(nifflerClient?.emails),
        ...read_array_values(nifflerClient?.email),
        ...read_array_values(brandPayload?.emailAddresses),
        ...read_array_values(brandPayload?.emails),
        ...read_array_values(brandPayload?.email),
        ...read_array_values(leadPayload?.emails),
        ...read_array_values(leadPayload?.emailAddresses)
    ]);
    const accountData = {
        brandName,
        facebookPageName,
        selectedBusinessLabel,
        selectedBusinessValue,
        selectedBusinessProvider: read_ad_business_provider(selectedBusinessAsset),
        selectedAdBusinesses,
        selectedAdBusinessesKey: selectedAdBusinesses.map((business) => `${business.provider}:${business.value}`).join('|'),
        accountUserId,
        firstName: firstName || '-',
        lastName: lastName || '-',
        username,
        accountUsername: userIsUnregistered ? '' : username,
        registrationStatus,
        linkedLabels: read_sass_linked_labels(offerItem),
        syncStatusLabel: read_sass_connection_status(offerItem),
        userIsUnregistered,
        assignedCaller: seller_label(caller),
        assignedCallerValue: read_member_identifier(caller),
        caller: seller_label(seller),
        category,
        subcategory,
        location,
        phoneNumbers,
        emailAddresses
    };

    return accountData;
};

const sass_missing_required_fields = (offerItem = {}, assignedCaller = null) => {
    const accountData = sass_account_data(offerItem);
    const explicitCaller = sass_caller(offerItem);
    const missingFields = [];

    if (!accountData.firstName || accountData.firstName === '-') {
        missingFields.push('owner first name');
    }

    if (!accountData.lastName || accountData.lastName === '-') {
        missingFields.push('owner last name');
    }

    if (!offer_brand(offerItem)) {
        missingFields.push('brand');
    }

    if (!accountData.emailAddresses.length) {
        missingFields.push('email');
    }

    if (!accountData.phoneNumbers.length) {
        missingFields.push('phone');
    }

    if (!accountData.category) {
        missingFields.push('category');
    }

    if (!accountData.subcategory) {
        missingFields.push('subcategory');
    }

    if (!accountData.location) {
        missingFields.push('location');
    }

    if (!explicitCaller && !read_member_identifier(assignedCaller)) {
        missingFields.push('caller');
    }

    return missingFields;
};

const sass_required_fields_error = (offerItem = {}, t, assignedCaller = null) => {
    const missingFields = sass_missing_required_fields(offerItem, assignedCaller);

    if (!missingFields.length) {
        return '';
    }

    return `${t('Complete SASS fields before IN PROGRESS')}: ${missingFields.map((fieldName) => t(fieldName)).join(', ')}`;
};

const current_comment_user_label = (user = null) => {
    if (!user) {
        return '';
    }

    return read_first_display_value([
        user?.name,
        user?.fullName,
        `${user?.firstName || user?.firstname || ''} ${user?.lastName || user?.lastname || ''}`.trim(),
        user?.displayName,
        user?.username,
        user?.preferred_username,
        user?.email
    ], '');
};

const Sass_Actions = (props) => {
    return (
        <SassBoardActions>
            <SassBoardSearchTeam>
                <SassBoardSearch>
                    <Input_
                        value={props.searchValue}
                        onChange={(event) => props.onSearchChange(event.target.value)}
                        placeholder={props.t('Search')}
                    />
                </SassBoardSearch>
                <SassBoardTeam>
                    {props.sellerOptions.map((sellerItem) => (
                        <SassBoardTeamMember
                            key={sellerItem.key}
                            title={sellerItem.label}
                            active={props.selectedSellerKeys.includes(sellerItem.key)}
                            onClick={() => props.onSellerClick(sellerItem.key)}
                        >
                            <Sass_Seller_Avatar seller={sellerItem.seller} size="2.8rem" />
                        </SassBoardTeamMember>
                    ))}
                </SassBoardTeam>
            </SassBoardSearchTeam>
        </SassBoardActions>
    );
};
